import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { ImageCropData, RecordData, Service } from 'webapp-types';
import {
	ModelName,
	NewReportSlideFragmentLayout,
	ReportSlideFragmentType,
} from 'webapp/enums/enums';
import { getOrderFromPosition, getPositionFromOrder } from 'webapp/utils/common';

import type Block from 'webapp/models-custom/block';
import type Board from 'webapp/models-custom/board';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import { DEFAULT_REPORT_TITLE } from 'webapp/models-custom/report';
import type ReportSlide from 'webapp/models-custom/report-slide';
import type ReportSlideFragmentRecord from 'webapp/models/report-slide-fragment';

import { Severity } from 'webapp/services/logger';

export default class ReportSlideFragment extends BufferedModel<
	ReportSlideFragmentRecord,
	RecordData.ReportSlideFragment.Serialized
> {
	@service('logger') declare loggerService: Service.Logger;
	@service('constants') declare constantsService: Service.Constants;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;
	@service('model-manager/report-slide-fragments')
	declare mmReportSlideFragmentsService: Service.ModelManager.ReportSlideFragments;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-previews')
	declare mmBoardPreviewsService: Service.ModelManager.BoardPreviews;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('billing') declare billingService: Service.Billing;

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model.id;
	}

	get type(): number {
		return this.getBuffered('type');
	}

	set type(value: number) {
		this.setBuffered('type', value);
	}

	get reportSlideId(): number {
		return this.getBuffered('reportSlideId');
	}

	set reportSlideId(value: number) {
		this.setBuffered('reportSlideId', value);
	}

	get order(): number {
		return this.getBuffered('order');
	}

	set order(value: number) {
		this.setBuffered('order', value);
	}

	get referenceId(): number {
		return this.getBuffered('referenceId');
	}

	set referenceId(value: number) {
		this.setBuffered('referenceId', value);
	}

	get value(): RecordData.ReportSlideFragment.Value {
		return this.getBuffered('value');
	}

	set value(value: RecordData.ReportSlideFragment.Value) {
		// validate the keys we're setting
		// log invalid, but set anyways
		const isKeyValid = Object.keys(value).every((key) => {
			return this.validValueKeys.includes(key);
		});
		if (!isKeyValid) {
			this.loggerService.logError(
				'Setting invalid value for report slide fragment',
				{
					reportSlideFragmentId: this.id,
					reportSlideFragmentType: this.type,
					invalidValue: JSON.stringify(value),
				},
				Severity.ERROR,
			);
		}
		this.setBuffered('value', value);
	}

	set changeText(value: string | null) {
		this.setBuffered('changeText', value);
	}

	get width(): number {
		return this.getBuffered('width');
	}

	set width(value: number) {
		this.setBuffered('width', value);
	}

	get height(): number {
		return this.getBuffered('height');
	}

	set height(value: number) {
		this.setBuffered('height', value);
	}

	get isNewlyCreated(): boolean {
		return this.getBuffered('isNewlyCreated');
	}

	set isNewlyCreated(value: boolean) {
		this.setBuffered('isNewlyCreated', value);
	}

	// ------- other props ------

	get slide(): ReportSlide | null {
		let reportSlide;
		if (this.reportSlideId) {
			reportSlide = this.mmReportSlidesService.findModel(String(this.reportSlideId));
			if (!reportSlide) {
				this.loggerService.logError(
					'Missing report slide for report slide fragment',
					{
						reportSlideFragmentId: this.id,
						reportSlideId: this.reportSlideId,
					},
					Severity.WARNING,
				);
			}
		}
		return reportSlide || null;
	}

	get validValueKeys(): string[] {
		switch (this.type) {
			case ReportSlideFragmentType.TITLE:
				return ['content'];
			case ReportSlideFragmentType.IMAGE:
				return ['imageUrl', 'imageOriginalUrl', 'imageCrop'];
			case ReportSlideFragmentType.BOARD:
				return ['previewHash'];
			case ReportSlideFragmentType.BLOCK:
				return ['color', 'themeId'];
			default:
				return []; // must be empty
		}
	}

	get primaryValue(): string | null {
		// the first valid key: content, imageUrl, previewHash or color, all strings
		const key = this.validValueKeys[0] as
			'content' | 'imageUrl' | 'previewHash' | 'color' | undefined;
		let primaryValue;
		if (key) {
			primaryValue = this.value?.[key];
		}
		return primaryValue || null;
	}

	get isStretchableOnSlide() {
		// don't stretch image on cover slide https://app.asana.com/0/1138028810573038/1208973045956657/f
		return (
			this.isTypeBlock ||
			this.isTypePlaceholder ||
			(this.isTypeImage && !this.slide?.isTypeCover)
		);
	}

	get isEmpty(): boolean {
		if (this.isTypeStoryText) {
			// text & data story
			const block = this.block;
			return !block?.text || block.text.trim() === '';
		} else if (this.isTypeTitle) {
			// does title exist and does it match the default report title?
			return !this.value.content || this.value.content === DEFAULT_REPORT_TITLE;
		} else if (this.isTypeBoard || this.isTypeBlock) {
			// board, metric, ai summary is never empty
			return false;
		}

		// image handled here
		return Object.keys(this.value).length === 0;
	}

	@cached
	get report() {
		return this.slide?.report;
	}

	@cached
	get isOnPagelessReport() {
		return this.report?.isPageless;
	}

	// ------- text-specific ------

	get isTypeTitle(): boolean {
		return this.type === ReportSlideFragmentType.TITLE;
	}

	updateText(content: string): void {
		this.value = { content };
	}

	// used to synch report title with fragment text
	updateChangeText(text: string): void {
		this.changeText = text;
	}

	// ------- image-specific ------

	get isTypeImage(): boolean {
		return this.type === ReportSlideFragmentType.IMAGE;
	}

	get isImageShown(): boolean {
		return !!this.primaryValue;
	}

	get isImageProportionalToSlide() {
		// image size should be proportional to the size of the slide when snapshotted (width 1920)
		return true; // !!this.slide?.isTypeImage;
	}

	updateImage(
		imageUrl: string,
		imageOriginalUrl = '',
		imageCrop: ImageCropData | null = null,
	): void {
		const value: RecordData.ReportSlideFragment.Value = { imageUrl };
		if (imageOriginalUrl) {
			value.imageOriginalUrl = imageOriginalUrl;
		}
		if (imageCrop) {
			value.imageCrop = imageCrop;
		}
		this.value = value;
	}

	// ------- board-specific ------

	get isTypeBoard(): boolean {
		return this.type === ReportSlideFragmentType.BOARD;
	}

	get isTypePlaceholder(): boolean {
		return this.type === ReportSlideFragmentType.PLACEHOLDER;
	}

	get boardId(): number | null {
		return this.isTypeBoard ? this.referenceId : null;
	}

	get previewHash(): string | null {
		return this.primaryValue;
	}

	@cached
	get board(): Board | null {
		// ignoring preview - use this for everything except data
		let board;
		if (this.boardId) {
			// must exist!
			board = this.mmBoardsService.boardModels.find(
				(item) => item.id === String(this.boardId),
			);

			// if (!board && !this.previewHash) {
			// 	this.loggerService.logError('Missing board for report slide fragment', {
			// 		reportSlideId: this.reportSlideId,
			// 		reportId: this.slide?.reportId,
			// 		boardId: this.boardId
			// 	}, Severity.WARNING);
			// }
		}
		return board || null;
	}

	get boardOrPreview(): Board | null {
		// use this for data
		return this.getBoardOrPreviewForHash(this.previewHash);
	}

	getBoardOrPreviewForHash(hash: string | null) {
		let board;
		if (this.boardId) {
			if (hash) {
				board = this.mmBoardPreviewsService.boardModels.find(
					(item) => item.previewHash === hash,
				);
			} else {
				board = this.mmBoardsService.boardModels.find(
					(item) => item.id === String(this.boardId),
				);
			}
		}
		return board || null;
	}

	requestBoardPreview(): Promise<boolean> {
		if (this.previewHash && this.boardId) {
			return this.mmBoardPreviewsService.requestPreview(this.previewHash, this.boardId);
		}
		return Promise.resolve(false);
	}

	updatePreviewHash(previewHash: string | null): void {
		this.value = { previewHash };
	}

	// ------- block-specific ------

	get isTypeBlock(): boolean {
		return this.type === ReportSlideFragmentType.BLOCK;
	}

	get blockColor(): string {
		return this.value.color || this.constantsService.defaultBoardColor;
	}

	get blockThemeId(): number {
		return this.value.themeId || 1;
	}

	get blockBgTheme(): string {
		return this.blockColorConfig.bgTheme;
	}

	get blockColorConfig() {
		return this.mmBoardsService.getColorConfig(this.blockColor, this.blockThemeId);
	}

	get blockColorGradient(): string {
		return this.mmBoardsService.getColorGradient(this.blockColor);
	}

	@cached
	get isFragmentWithMetricData(): boolean {
		return this.isTypeBlock || (this.isTypeStoryText && !!this.block?.metricDataArray.length);
	}

	getColorConfig(color: string, themeId: number) {
		return this.mmBoardsService.getColorConfig(color, themeId);
	}

	updateBlockColor(color: string): void {
		this.value = Object.assign({}, this.value, { color });
	}

	updateBlockThemeId(themeId: number): void {
		this.value = Object.assign({}, this.value, { themeId });
	}

	// ------- story-text-specific ------

	get isTypeStoryText() {
		return this.type === ReportSlideFragmentType.STORY_TEXT;
	}

	// ------- block AND story-text-specific ------

	get blockId(): number | null {
		// block AND story-text
		return this.isTypeBlock || this.isTypeStoryText ? this.referenceId : null;
	}

	@cached
	get block(): Block | null {
		let block;
		if (this.blockId) {
			block = this.mmBlocksService.findModel(String(this.blockId))!;
			// if (!block) {
			// 	this.loggerService.logError('Missing block for report slide fragment', {
			// 		reportSlideId: this.reportSlideId,
			// 		reportId: this.slide?.reportId,
			// 		blockId: this.blockId
			// 	}, Severity.WARNING);
			// }
		}
		return block || null;
	}

	requestBlockData() {
		const reportModel = this.mmReportSlidesService.findModel(
			String(this.reportSlideId),
		)?.report;

		if (
			(reportModel?.isPublicTemplate && !this.accountService.isPublictemplatesSpace) ||
			this.navigationService.isDatawallApp
		) {
			return Promise.resolve(); // Prevent fetching data from blocks endpoint, because we get data from token endpoint
		}

		// Prevent fetching data if there is only text in story text block
		if (this.isTypeStoryText) {
			const block = this.mmBlocksService.findModel(String(this.blockId));

			if (block && !block.fromMetricSetup) {
				return Promise.resolve();
			}
		}

		return this.mmBlocksService.requestSingleBlock(String(this.blockId));
	}

	// ------- limitations ------

	get isDisabled(): boolean {
		return !!this.disabledLimitation;
	}

	get disabledLimitation() {
		if (this.report?.isTemplate) {
			return null;
		}

		let presumedLayout: NewReportSlideFragmentLayout | undefined;
		if (this.isTypeStoryText) {
			if (this.block?.dataSelects.length) {
				presumedLayout = NewReportSlideFragmentLayout.DATA_STORY;
			} else {
				presumedLayout = NewReportSlideFragmentLayout.TEXT;
			}
		} else if (this.isTypeBlock) {
			if (this.block?.isAiSummary) {
				presumedLayout = NewReportSlideFragmentLayout.AI_PERFORMANCE_SUMMARY;
			} else {
				presumedLayout = NewReportSlideFragmentLayout.METRIC;
			}
		} else if (this.isTypeImage) {
			presumedLayout = NewReportSlideFragmentLayout.IMAGE;
		}
		const limitationToCheck = presumedLayout
			? this.mmReportSlideFragmentsService.getLimitationFromNewFragmentLayout(presumedLayout)
			: null;

		if (limitationToCheck && !this.billingService.checkIfCan(limitationToCheck)) {
			return limitationToCheck;
		} else {
			return null;
		}
	}

	// ------- flexible layout ------

	@cached
	get position() {
		return getPositionFromOrder(this.order);
	}

	get globalOrder() {
		// order across the whole report
		const globalFirstFragmentRow = this.slide?.globalFirstFragmentRow || 0,
			position = this.position;
		return getOrderFromPosition(globalFirstFragmentRow + position.row, position.column);
	}

	set globalOrder(globalOrder: number) {
		const order = this.slide!.getOrderFromGlobalOrder(globalOrder);
		this.order = order;
	}

	@cached
	get globalPosition() {
		// position across the whole report
		return getPositionFromOrder(this.globalOrder);
	}

	@cached
	get fragmentsInRow() {
		const reportSlide = this.slide;
		if (!reportSlide) {
			return [];
		}

		return reportSlide.fragments.filter(
			(fragment) => fragment.position.row === this.position.row,
		);
	}

	@cached
	get isAddColumnShown() {
		return this.fragmentsInRow.length < 3 && !this.isTypeTitle;
	}

	@cached
	get isLastColumnInRow(): boolean {
		if (this.isDeletedState()) {
			return true;
		}

		const fragmentsInRow = this.fragmentsInRow;

		const lastColumnFragment = fragmentsInRow.reduce((lastFragment, currentFragment) => {
			return !lastFragment || currentFragment.order > lastFragment.order
				? currentFragment
				: lastFragment;
		}, fragmentsInRow[0]);

		// no row left when the slide was just deleted while its fragments still render
		if (!lastColumnFragment) {
			return true;
		}

		return lastColumnFragment.order === this.order;
	}

	@cached
	get isThreeColumnRow(): boolean {
		const fragmentsInRow = this.fragmentsInRow;
		return fragmentsInRow.length === 3;
	}

	// -----------------------------

	override save(force = false): Promise<ReportSlideFragmentRecord> {
		return super.save(force);
	}

	static override modelName = ModelName.REPORT_SLIDE_FRAGMENT; // store record type
}
